///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useSelector } from "react-redux";

import AutoComplete from "../form-controls/AutoComplete";
import OutlinedInput from "../form-controls/OutlinedInput";
import {
  fscTypes,
  containerSizeTypes,
  imdlSolutionTypes,
  mode,
  division,
  currencyTypes,
  bidScacType,
  bidSubmissionTypes
} from "../../helpers/types";
import { getFscForShipper } from "../../api/shippers";
import Spinner from "../icons/Spinner";
import disableRFQStatus, {
  getClosedBidReopenBlockedTooltipContent
} from "../../helpers/disableRFQStatus";
import messages from "../../helpers/messages";
import Tooltip from "../Tooltip";

function Intermodal({
  modeDetails,
  setModeDetails,
  validationData,
  removeError,
  shipper,
  role,
  summary,
  bidScac
}) {
  const { featureFlags: { crmEnableImdlUi } } = useSelector(state => state);

  const [fscPegSpinner, toggleFscPegSpinner] = useState(false);
  const [fscFocused, toggleFscFocused] = useState(false);
  const showImdlCrmNames = crmEnableImdlUi && bidScac?.value === bidScacType[1].value;

  const handleValidation = ({ target }) => {
    if (
      isNaN(parseFloat(target.value, 10)) ||
      parseFloat(target.value, 10) > target.max ||
      parseFloat(target.value, 10) < target.min
    ) {
      setModeDetails(mode.imdl, target.name, "");
    } else {
      setModeDetails(mode.imdl, target.name, parseFloat(target.value, 10));
    }
  };

  const commonProps = field => ({
    name: field,
    value: modeDetails[field],
    onFocus: () => !validationData[field].isValid && removeError("imdl", field),
    error: !validationData[field].isValid,
    validationMessage: validationData[field].validationMessage
  });

  const optionalProps = field => ({
    name: field,
    value: modeDetails[field],
    error: false,
    validationMessage: ""
  });

  const getCoyoteFsc = () => {
    if (shipper) {
      toggleFscPegSpinner(true);
      getFscForShipper(shipper.shipperId)
        .then(({ data: fscPeg }) => {
          toggleFscPegSpinner(false);
          if (parseFloat(fscPeg)) {
            setModeDetails(
              mode.imdl,
              "fuelSurchargePeg",
              parseFloat(fscPeg).toFixed(2)
            );
            !validationData.fuelSurchargePeg.isValid &&
              removeError("imdl", "fuelSurchargePeg");
          }
        })
        .catch(() => toggleFscPegSpinner(false));
    }
  };

  const handleNumberChange = ({ target }) =>
    !target.validity.patternMismatch &&
    setModeDetails(mode.imdl, target.name, target.value);

  const handleTrimText = ({ target }) => {
    target && target.value
      ? setModeDetails(mode.imdl, target.name, target.value.trim())
      : setModeDetails(mode.imdl, target.name, "");
  };

  const closedBidReopenTooltip = getClosedBidReopenBlockedTooltipContent(
    modeDetails,
    summary
  );
  const rfqStatusCell = (
    <div data-testid="rfq-status-container" className="grid__cell one-half">
      <AutoComplete
        isDisabled={disableRFQStatus(mode.imdl, modeDetails, role, summary)}
        label={messages.rfqStatus.defaultMessage}
        value={modeDetails.currentStatus}
        staticOptions={modeDetails.rfqStatusOptions}
        handleChange={value =>
          setModeDetails(mode.imdl, "currentStatus", value)
        }
      />
    </div>
  );

  return (
    <div className="intermodal grid section-grid">
      {closedBidReopenTooltip ? (
        <Tooltip content={closedBidReopenTooltip}>{rfqStatusCell}</Tooltip>
      ) : (
        rfqStatusCell
      )}
      <div className="grid__cell one-half">
        <OutlinedInput
          label={messages.numberOfLanes.defaultMessage}
          type="text"
          pattern="^[1-9]\d{0,5}$"
          min="1"
          max="999999"
          onBlur={handleValidation}
          onChange={handleNumberChange}
          {...commonProps("laneCount")}
        />
      </div>
      <div className="grid__cell one-half">
        <OutlinedInput
          label={messages.volume.defaultMessage}
          type="text"
          pattern="^[1-9]\d{0,8}$"
          min="1"
          max="999999999"
          onBlur={handleValidation}
          onChange={handleNumberChange}
          {...commonProps("volume")}
        />
      </div>
      <div data-testid="fsc-type-container" className="grid__cell one-half">
        <AutoComplete
          label={crmEnableImdlUi ? messages.fscTypeOptional.defaultMessage : messages.fscType.defaultMessage}
          handleChange={value =>
            setModeDetails(mode.imdl, "fuelSurchargeType", value)
          }
          staticOptions={fscTypes.filter(
            x => !x.division || x.division === division.coy
          )}
          {...(crmEnableImdlUi ? optionalProps("fuelSurchargeType") : commonProps("fuelSurchargeType"))}
        />
      </div>
      <div className="grid__cell one-half">
        <OutlinedInput
          data-testid="fsc-peg"
          label={crmEnableImdlUi ? messages.fscPegOptional.defaultMessage : messages.fscPeg.defaultMessage}
          type="text"
          pattern="^(\d{1}|\d{0,1}\.\d{0,2})$"
          min="0.01"
          max="9.99"
          unit="$"
          isUnitPositionLeft={true}
          onBlur={e => {
            toggleFscFocused(false);
            handleValidation(e);
          }}
          onChange={handleNumberChange}
          {...(crmEnableImdlUi ? optionalProps("fuelSurchargePeg") : commonProps("fuelSurchargePeg"))}
          value={
            fscFocused
              ? modeDetails.fuelSurchargePeg
              : modeDetails.fuelSurchargePeg &&
                !isNaN(modeDetails.fuelSurchargePeg)
              ? parseFloat(modeDetails.fuelSurchargePeg).toFixed(2)
              : ""
          }
          onFocus={() => {
            !validationData.fuelSurchargePeg.isValid &&
              removeError("imdl", "fuelSurchargePeg");
            toggleFscFocused(true);
          }}
        >
          {fscPegSpinner || (
            <button onClick={getCoyoteFsc} className="fsc-peg-helper">
              {messages.useCoyoteFsc.defaultMessage}
            </button>
          )}
          {fscPegSpinner && (
            <Spinner
              className="spinner fsc-peg-helper"
              height="16px"
              width="16px"
              fill="#0088D1"
            />
          )}
        </OutlinedInput>
      </div>
      {/* {bidScac.value === bidScacType[1].value ? (
        <>
          <div className="grid__cell one-half">
            <OutlinedInput
              id="rxoFuel"
              label={messages.rxoFuel.defaultMessage}
              pattern="^(\d{1}|\d{0,1}\.\d{0,2})$"
              min="0.01"
              max="9.99"
              unit="$"
              isUnitPositionLeft={true}
              onBlur={handleValidation}
              onChange={handleNumberChange}
              {...commonProps("rxoFuel")}
            />
          </div>
          <div className="grid__cell one-half">
            <OutlinedInput
              id="customerFuel"
              label={messages.customerFuel.defaultMessage}
              pattern="^(\d{1}|\d{0,1}\.\d{0,2})$"
              min="0.01"
              max="9.99"
              unit="$"
              isUnitPositionLeft={true}
              onBlur={handleValidation}
              onChange={handleNumberChange}
              {...commonProps("customerFuel")}
            />
          </div>
        </>
      ) : null} */}
      <div
        data-testid="container-size-container"
        className="grid__cell one-half"
      >
        <AutoComplete
          label={showImdlCrmNames ? messages.solution.defaultMessage : messages.containerSize.defaultMessage}
          handleChange={value =>
            setModeDetails(mode.imdl, "equipmentLengths", value)
          }
          staticOptions={showImdlCrmNames ? imdlSolutionTypes : containerSizeTypes}
          isMulti
          {...commonProps("equipmentLengths")}
        />
      </div>
      <div className="grid__cell one-half">
        <OutlinedInput
          label={messages.typicalPalletCountOptional.defaultMessage}
          type="number"
          step="1"
          min="0"
          max="999999"
          onBlur={handleValidation}
          onChange={event =>
            setModeDetails(
              mode.imdl,
              "palletCount",
              parseInt(event.target.value, 10)
            )
          }
          name="palletCount"
          value={modeDetails.palletCount}
        />
      </div>
      <div
        data-testid="additional-currencies-container"
        className="grid__cell one-half"
      >
        <AutoComplete
          label={messages.additionalCurrenciesOptional.defaultMessage}
          value={modeDetails.additionalCurrencies}
          isMulti
          handleChange={value =>
            setModeDetails(mode.imdl, "additionalCurrencies", value)
          }
          staticOptions={currencyTypes}
        />
      </div>
      <div className="grid__cell one-half">
        <OutlinedInput
          name="competitors"
          data-testid="competitors"
          label={messages.competitorsOptional.defaultMessage}
          value={modeDetails.competitors}
          maxLength="1000"
          onChange={event =>
            setModeDetails(mode.imdl, "competitors", event.target.value)
          }
          onBlur={handleTrimText}
          isMulti
          dynamicHeight
        />
      </div>
      <div className="grid__cell one-half mb-24">
        <OutlinedInput
          name="notes"
          data-testid="notes"
          label={showImdlCrmNames ? messages.bidDetailsOptional.defaultMessage : messages.additionalInformationOptional.defaultMessage}
          maxLength="3000"
          onChange={event =>
            setModeDetails(mode.imdl, "notes", event.target.value)
          }
          isMulti
          value={modeDetails.notes}
          onBlur={handleTrimText}
          dynamicHeight
        />
      </div>
      {crmEnableImdlUi && (
        <>
          <div className="grid__cell one-half">
            <AutoComplete
              id="opportunityCurrency"
              label={messages.opportunityCurrency.defaultMessage}
              handleChange={value =>
                setModeDetails(mode.imdl, "currencyType", value)
              }
              value={modeDetails.currencyType}
              staticOptions={currencyTypes}
              onFocus={() =>
                !validationData.currencyType.isValid &&
                removeError("imdl", "currencyType")
              }
              error={!validationData.currencyType.isValid}
              validationMessage={validationData.currencyType.validationMessage}
            />
          </div>
          <div className="grid__cell one-half">
            <AutoComplete
              id="bidSubmissionType"
              label={messages.bidSubmissionType.defaultMessage}
              handleChange={value =>
                setModeDetails(mode.imdl, "bidSubmissionType", value?.value ?? 0)
              }
              value={
                bidSubmissionTypes.find(
                  x => x.value === modeDetails.bidSubmissionType
                ) || bidSubmissionTypes[0]
              }
              staticOptions={bidSubmissionTypes}
              isClearable={false}
            />
          </div>
          {modeDetails.bidSubmissionType === 1 && (
            <div className="grid__cell one-whole">
              <OutlinedInput
                id="bidLoginDetails"
                name="bidLoginDetails"
                value={modeDetails.bidLoginDetails || ""}
                label={messages.bidLoginDetails.defaultMessage}
                maxLength={3000}
                isMulti={true}
                rows={6}
                onChange={event =>
                  setModeDetails(mode.imdl, "bidLoginDetails", event.target.value)
                }
                onBlur={handleTrimText}
                dynamicHeight
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Intermodal;
